import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import {
  X,
  Mail,
  Lock,
  User,
  ShieldCheck,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import dynamic from 'next/dynamic';
const PhoneInput = dynamic(() => import('react-phone-number-input'), {
  ssr: false,
});
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';

import { useAppAuth } from '@/hooks/useAppAuth';
import { useEffect } from 'react';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

type AuthStep = 'login' | 'signup' | 'verify_sent' | 'forgot' | 'reset_sent';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }: LoginModalProps) => {
  const { isAuthenticated, isAuthLoading, authError } = useAppAuth();

  const [activeStep, setActiveStep] = useState<AuthStep>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) window.dispatchEvent(new Event('baasil:auth-needed'));
  }, [isOpen]);

  // Automatically close if authenticated (e.g. from redirect)
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      onClose();
    }
  }, [isAuthenticated, isOpen, onClose]);

  const isSignUp = activeStep === 'signup';
  const isForgot = activeStep === 'forgot';
  const isVerifySent = activeStep === 'verify_sent';
  const isResetSent = activeStep === 'reset_sent';

  const resetForm = () => {
    setActiveStep('login');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setPhoneNumber('');
    setError('');
    setMessage('');
    setIsLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // ----- Google Sign-In -----
  const handleGoogleLogin = async () => {
    if (isLoading) return;
    setError('');
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();

      // Try popup first on all platforms (including iOS - modern Safari supports
      // popups triggered from user click events). Fall back to redirect if the
      // popup is blocked or otherwise fails.
      try {
        const result = await signInWithPopup(auth, provider);
        if (result.user) {
          const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
          document.cookie = `auth_session=true; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax${isSecure ? '; Secure' : ''}`;
          handleClose();
          onLoginSuccess?.();
        }
      } catch (popupErr: any) {
        if (popupErr.code === 'auth/popup-blocked') {
          await signInWithRedirect(auth, provider);
        } else if (popupErr.code !== 'auth/popup-closed-by-user') {
          // Popup failed for a reason other than user closing it - fall back
          // to redirect (covers iOS popup compatibility issues gracefully).
          await signInWithRedirect(auth, provider);
        } else {
          setIsLoading(false);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
      setIsLoading(false);
    }
  };

  // ----- Email / Password Sign-In -----
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Set a session cookie to help middleware bypass landing disable
      document.cookie = `auth_session=true; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
      handleClose();
      onLoginSuccess?.();
    } catch (err: any) {
      const msg =
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/user-not-found'
          ? 'Invalid email or password'
          : err.message || 'Sign in failed';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ----- Sign Up -----
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Set display name
      if (fullName.trim()) {
        await updateProfile(user, { displayName: fullName.trim() });
      }

      // Send verification email
      await sendEmailVerification(user);

      setActiveStep('verify_sent');
      setMessage(
        'Account created! Check your email for a verification link before signing in.',
      );
    } catch (err: any) {
      const msg =
        err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists.'
          : err.message || 'Sign up failed';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ----- Forgot Password -----
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setActiveStep('reset_sent');
      setMessage(
        `Password reset link sent to ${email}. Check your inbox and follow the link.`,
      );
    } catch (err: any) {
      const msg =
        err.code === 'auth/user-not-found'
          ? 'No account found with this email.'
          : err.message || 'Failed to send reset email';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTitle = (): string =>
    ({
      login: 'Welcome back',
      signup: 'Create an account',
      verify_sent: 'Check your email',
      forgot: 'Forgot Password',
      reset_sent: 'Reset email sent',
    })[activeStep];

  const getSubtitle = (): string =>
    ({
      login: 'Enter your email to sign in to your account',
      signup: 'Enter your details below to create your account',
      verify_sent: `Verification link sent to ${email}`,
      forgot: 'Enter your email to receive a password reset link',
      reset_sent: `Reset link sent to ${email}`,
    })[activeStep];

  return (
    <AnimatePresence>
      <div
        key="login-modal-overlay"
        className="fixed inset-0 z-[200] flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="font-mono relative flex flex-col md:flex-row bg-white w-full max-w-4xl md:h-[580px] rounded-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Left panel */}
          <div className="hidden md:block md:w-1/2 relative bg-slate-100 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#1a5b9b]/90 to-transparent z-10" />
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center p-6 text-center"
              style={{ color: 'white' }}
            >
              <ShieldCheck className="w-12 h-12 mb-3 opacity-90" />
              <h2 className="text-2xl font-display uppercase tracking-widest leading-tight mb-2">
                Secure <br /> Your Checkout
              </h2>
              <p className="font-mono text-[13px] font-medium opacity-80 max-w-xs">
                Log in to effortlessly track your orders, access fast checkouts,
                and manage your orders.
              </p>
            </div>
          </div>

          {/* Right panel */}
          <div
            className={`w-full md:w-1/2 p-5 md:p-8 bg-white relative flex flex-col overflow-y-auto ${isSignUp ? 'py-8' : 'justify-center'}`}
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors p-2"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-6 text-center">
              <img
                src="/assets/logo.svg"
                alt="Bassil"
                className={`${isSignUp ? 'h-14' : 'h-28'} mx-auto mb-3 object-contain`}
              />
              <h3 className="text-2xl font-bold tracking-tight text-slate-900">
                {getTitle()}
              </h3>
              <p className="text-[13px] font-medium text-slate-600 mt-1.5">
                {getSubtitle()}
              </p>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-600 text-[13px] font-medium animate-in fade-in slide-in-from-top-1">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <p>{error}</p>
              </div>
            )}
            {message && (
              <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 text-emerald-600 text-[13px] font-medium animate-in fade-in slide-in-from-top-1">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <p>{message}</p>
              </div>
            )}



            {/* ── Login Form ── */}
            {activeStep === 'login' && (
              <form className="space-y-4" onSubmit={handleLogin}>
                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="m@example.com"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[13px] font-semibold leading-none text-slate-800">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveStep('forgot');
                        setError('');
                        setMessage('');
                      }}
                      className="text-[12px] font-semibold text-slate-700 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-[14px] font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-slate-900 text-slate-50 hover:bg-slate-900/90 h-11 px-4 w-full mt-2 gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Sign In'
                  )}
                </button>

                {process.env.NEXT_PUBLIC_DISABLE_LANDING_PAGE !== 'true' && (
                  <>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center text-[11px] uppercase">
                        <span className="bg-white px-2 text-slate-500 font-semibold tracking-wider">
                          Or continue with
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleGoogleLogin}
                      disabled={isLoading}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-[14px] font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-slate-300 bg-white hover:bg-slate-100 hover:text-slate-900 h-11 px-4 w-full gap-2"
                    >
                      <img
                        src="/assets/google-color.svg"
                        className="w-4 h-4"
                        alt="Google"
                      />
                      Google
                    </button>
                  </>
                )}

                {process.env.NEXT_PUBLIC_DISABLE_LANDING_PAGE !== 'true' && (
                  <div className="mt-6 text-center text-[13px] font-medium text-slate-600">
                    Don&apos;t have an account?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setActiveStep('signup');
                        setError('');
                        setMessage('');
                      }}
                      className="font-bold text-slate-900 hover:underline"
                    >
                      Sign up
                    </button>
                  </div>
                )}
              </form>
            )}

            {/* ── Sign Up Form ── */}
            {activeStep === 'signup' && (
              <form className="space-y-4" onSubmit={handleSignUp}>
                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Doe"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Mobile Number
                  </label>
                  <div className="phone-input-container">
                    <PhoneInput
                      placeholder="+94 7X XXX XXXX"
                      value={phoneNumber}
                      onChange={(value) => setPhoneNumber(value || '')}
                      international
                      defaultCountry="LK"
                      flags={flags}
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[14px] font-medium ring-offset-white transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="m@example.com"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-[14px] font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-slate-900 text-slate-50 hover:bg-slate-900/90 h-11 px-4 w-full mt-2 gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Create account'
                  )}
                </button>

                <div className="mt-6 text-center text-[13px] font-medium text-slate-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveStep('login');
                      setError('');
                      setMessage('');
                    }}
                    className="font-bold text-slate-900 hover:underline"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            )}

            {/* ── Forgot Password Form ── */}
            {activeStep === 'forgot' && (
              <form className="space-y-4" onSubmit={handleForgotPassword}>
                <div className="space-y-2">
                  <label className="text-[13px] font-semibold leading-none text-slate-800">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="m@example.com"
                      className="flex h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-[14px] font-medium ring-offset-white placeholder:text-slate-400 placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-mono"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-[14px] font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-slate-900 text-slate-50 hover:bg-slate-900/90 h-11 px-4 w-full mt-2 gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveStep('login');
                    setError('');
                    setMessage('');
                  }}
                  className="w-full text-center text-[13px] font-medium text-slate-500 hover:text-slate-800 transition-colors"
                >
                  Back to Sign In
                </button>
              </form>
            )}

            {/* ── Verify Sent / Reset Sent info screens ── */}
            {(activeStep === 'verify_sent' || activeStep === 'reset_sent') && (
              <div className="space-y-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setActiveStep('login');
                    setError('');
                    setMessage('');
                  }}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-[14px] font-semibold ring-offset-white transition-colors bg-slate-900 text-slate-50 hover:bg-slate-900/90 h-11 px-4 w-full gap-2"
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
      <style jsx global>{`
        .phone-input-container .PhoneInput {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .phone-input-container .PhoneInputCountry {
          display: flex;
          align-items: center;
          padding-right: 8px;
          border-right: 1px solid #e2e8f0;
          cursor: pointer;
        }
        .phone-input-container .PhoneInputCountrySelect {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          opacity: 0;
          cursor: pointer;
          z-index: 10;
        }
        .phone-input-container .PhoneInputCountryIcon {
          width: 24px;
          height: 16px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
        .phone-input-container .PhoneInputInput {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-family: inherit;
          font-size: 14px;
          font-weight: 500;
          color: #0f172a;
        }
        .phone-input-container .PhoneInputInput::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }
      `}</style>
    </AnimatePresence>
  );
};

export default LoginModal;
